"use client";

import { useState } from "react";
import { User, LogIn, Settings, FileText, HelpCircle, ChevronRight, LogOut, Plane, Heart, MapPin, Star, Building2 } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/i18n/I18nProvider";
import { useFavorites } from "@/hooks/useFavorites";
import PageHeader from "@/components/layout/PageHeader";

const fieldBase = "w-full h-12 rounded-[14px] border border-[#E3E9E9] bg-white px-4 text-[14px] text-text-dark outline-none focus:border-primary transition-colors";

export default function MePage() {
  const { t } = useI18n();
  const [loggedIn, setLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const { favoriteHospitals, toggleFavorite } = useFavorites();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginForm.email && loginForm.password) {
      setLoggedIn(true);
      setShowLogin(false);
    }
  };

  if (showLogin) {
    return (
      <div className="pb-24">
        <PageHeader
          title={t("Sign In")}
          subtitle={t("Access your profile and bookings")}
          onBack={() => setShowLogin(false)}
        />
        <form onSubmit={handleLogin} className="px-5 py-5 space-y-4">
          <div>
            <label className="cc-label mb-1.5 block">{t("Email Address *")}</label>
            <input type="email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={fieldBase} placeholder="your@email.com" />
          </div>
          <div>
            <label className="cc-label mb-1.5 block">{t("Password")}</label>
            <input type="password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={fieldBase} placeholder={t("Enter password")} />
          </div>
          <button type="submit" className="cc-btn">{t("Sign In")}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <PageHeader title={t("My Profile")} subtitle={t("Your account and care services")}>
        {loggedIn ? (
          <div className="flex items-center gap-3 mt-4">
            <div className="w-14 h-14 rounded-full bg-primary-bg flex items-center justify-center">
              <User size={25} className="text-primary" />
            </div>
            <div>
              <p className="text-[16px] font-bold text-text-dark">John Smith</p>
              <p className="text-[12.5px] text-text-dark/50">john.smith@email.com</p>
              <p className="text-[12px] text-text-dark/35 mt-0.5">{t("Member since")} Oct 2026</p>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowLogin(true)}
            className="flex items-center gap-3 bg-primary-bg rounded-2xl p-4 w-full mt-4 text-left active:bg-primary-bg/70 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0">
              <LogIn size={21} className="text-primary" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-text-dark">{t("Sign In")}</p>
              <p className="text-[12.5px] text-text-dark/50 mt-0.5">{t("Access your profile and bookings")}</p>
            </div>
          </button>
        )}
      </PageHeader>

      <div className="px-5 py-4 space-y-2.5">
        {loggedIn && (
          <>
            <Link href="/bookings" className="flex items-center gap-3 cc-card p-4 active:bg-gray-50">
              <FileText size={19} className="text-primary shrink-0" />
              <span className="text-[14.5px] text-text-dark flex-1">{t("My Bookings")}</span>
              <ChevronRight size={15} className="text-text-dark/30" />
            </Link>

            {/* My Favorites */}
            <div className="cc-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Heart size={18} className="text-primary" />
                  <h3 className="text-[14.5px] font-bold text-text-dark">{t("My Favorites")}</h3>
                </div>
                <span className="text-[12px] text-text-dark/40 font-mono">{favoriteHospitals.length}</span>
              </div>
              {favoriteHospitals.length === 0 ? (
                <div className="py-4 text-center">
                  <p className="text-[13px] text-text-dark/45">{t("No favorites yet")}</p>
                  <Link href="/hospitals" className="mt-2 inline-block text-[13px] text-primary font-semibold">
                    {t("Browse hospitals")}
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {favoriteHospitals.map((h) => (
                    <Link key={h.id} href={`/hospitals/${h.id}`} className="flex items-center gap-3 p-2.5 rounded-xl bg-page-bg/60 active:bg-page-bg transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-primary-bg flex items-center justify-center shrink-0">
                        <Building2 size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold text-text-dark truncate">{h.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11.5px] text-text-dark/50 flex items-center gap-0.5">
                            <MapPin size={10} /> {h.city}
                          </span>
                          <span className="text-[11.5px] text-amber-600 flex items-center gap-0.5 font-semibold">
                            <Star size={10} className="fill-amber-400 text-amber-400" /> {h.rating}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(h.id); }}
                        aria-label={t("Remove from favorites")}
                        className="w-7 h-7 rounded-full flex items-center justify-center active:bg-red-50 shrink-0"
                      >
                        <Heart size={14} className="fill-red-500 text-red-500" />
                      </button>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 cc-card p-4">
              <Settings size={19} className="text-primary shrink-0" />
              <span className="text-[14.5px] text-text-dark flex-1">{t("Account Settings")}</span>
              <span className="text-[12px] text-text-dark/35">{t("Coming soon")}</span>
            </div>
          </>
        )}

        <Link href="/help" className="flex items-center gap-3 cc-card p-4 active:bg-gray-50">
          <HelpCircle size={19} className="text-primary shrink-0" />
          <span className="text-[14.5px] text-text-dark flex-1">{t("Help Center")}</span>
          <ChevronRight size={15} className="text-text-dark/30" />
        </Link>

        <Link href="/travel" className="flex items-center gap-3 cc-card p-4 active:bg-gray-50">
          <Plane size={19} className="text-primary shrink-0" />
          <span className="text-[14.5px] text-text-dark flex-1">{t("Travel Assistance")}</span>
          <ChevronRight size={15} className="text-text-dark/30" />
        </Link>

        <Link href="/services" className="flex items-center gap-3 cc-card p-4 active:bg-gray-50">
          <FileText size={19} className="text-primary shrink-0" />
          <span className="text-[14.5px] text-text-dark flex-1">{t("Medical Services")}</span>
          <ChevronRight size={15} className="text-text-dark/30" />
        </Link>

        {loggedIn && (
          <button onClick={() => setLoggedIn(false)}
            className="flex items-center gap-3 bg-white rounded-2xl p-4 w-full border border-red-100 shadow-[0_2px_10px_rgba(14,60,58,0.06)] active:bg-red-50">
            <LogOut size={19} className="text-red-500 shrink-0" />
            <span className="text-[14.5px] text-red-500 flex-1 text-left">{t("Sign Out")}</span>
          </button>
        )}
      </div>

      <div className="px-5 pb-4">
        <p className="text-[12px] text-text-dark/35 text-center leading-[1.6]">
          {t("ChinaCare+ coordinates medical services and does not provide diagnosis or treatment directly.")}
        </p>
      </div>
    </div>
  );
}
